// src/pages/ResetPassword.js
import React, { useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GlobalContext } from '../context/GlobalContext';
import axios from 'axios';
import { BsEye, BsEyeSlash } from 'react-icons/bs';
import IconsSubmit from '../components/styled/IconsSubmit';

const ResetPassword = () => {
  const { userid, token } = useParams();
  const navigate = useNavigate();
  const { API_URL, showToast } = useContext(GlobalContext);

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' }); // Clear error when typing
  };

  const validateForm = () => {
    const newErrors = {};

    // Password strength regex
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#$%])[A-Za-z\d@#$%]{8,}$/;

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (!passwordRegex.test(formData.newPassword)) {
      newErrors.newPassword = 'Min 8 chars: 1 uppercase, 1 lowercase, 1 number, 1 special (@#$%)';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    showToast('pending', 'Updating password...', 'top');

    try {
      const response = await axios.post(
        `${API_URL}/api/resetPassword/${userid}/${token}`,
        { newPassword: formData.confirmPassword } // backend expects "newPassword"
      );

      if (response.data.success) {
        showToast('success', 'Password changed successfully!', 'top', 8);
        setTimeout(() => navigate('/login'), 2000);
      } else {
        showToast('error', response.data.message || 'Failed to reset password', 'top');
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Invalid or expired link';
      showToast('failure', msg, 'top', 10);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Premium Card with 4-side Shadow */}
        <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-[0_0_20px_rgba(0,0,0,0.24)] ring-1 ring-gray-900/5">

          <h1 className="text-3xl font-bold text-center text-gray-900 mb-2">
            Reset Password
          </h1>
          <p className="text-center text-gray-600 mb-8">
            Enter your new secure password
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">

            {/* New Password */}
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                className={`peer w-full px-4 py-4 pr-12 text-gray-900 bg-transparent border-2 rounded-xl 
                  focus:outline-none transition-all duration-300 placeholder-transparent
                  ${errors.newPassword ? 'border-red-500' : 'border-gray-300 focus:border-blue-600'}`}
                placeholder="New Password"
                required
              />
              <label className={`absolute left-4 -top-2.5 px-2 bg-white text-sm font-medium text-gray-600 
                transition-all duration-300 peer-placeholder-shown:top-4 peer-placeholder-shown:text-base 
                peer-placeholder-shown:text-gray-400 peer-focus:-top-2.5 peer-focus:text-blue-600 peer-focus:text-sm
                ${formData.newPassword ? '-top-2.5 text-blue-600 text-sm' : ''}`}>
                New Password
              </label>

              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-4 text-gray-500 hover:text-gray-700"
              >
                {showNew ? <BsEye size={22} /> : <BsEyeSlash size={22} />}
              </button>

              {errors.newPassword && (
                <p className="mt-2 text-sm text-red-600">{errors.newPassword}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`peer w-full px-4 py-4 pr-12 text-gray-900 bg-transparent border-2 rounded-xl 
                  focus:outline-none transition-all duration-300 placeholder-transparent
                  ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300 focus:border-blue-600'}`}
                placeholder="Confirm Password"
                required
              />
              <label className={`absolute left-4 -top-2.5 px-2 bg-white text-sm font-medium text-gray-600 
                transition-all duration-300 peer-placeholder-shown:top-4 peer-placeholder-shown:text-base 
                peer-placeholder-shown:text-gray-400 peer-focus:-top-2.5 peer-focus:text-blue-600 peer-focus:text-sm
                ${formData.confirmPassword ? '-top-2.5 text-blue-600 text-sm' : ''}`}>
                Confirm Password
              </label>

              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-4 text-gray-500 hover:text-gray-700"
              >
                {showConfirm ? <BsEye size={22} /> : <BsEyeSlash size={22} />}
              </button>

              {errors.confirmPassword && (
                <p className="mt-2 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-4 rounded-xl font-semibold text-lg text-white shadow-lg transition-all duration-300
                ${isLoading 
                  ? 'bg-blue-400 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 hover:shadow-2xl hover:-translate-y-0.5'}`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-3">
                  <IconsSubmit />
                  Updating...
                </span>
              ) : (
                'Update Password'
              )}
            </button>
          </form>

          <p className="mt-8 text-center text-gray-600">
            <a href="/login" className="font-medium text-blue-600 hover:underline">
              Back to Login
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
